import {
  ActionError,
  Button,
  EmptyState,
  PageHeader,
  PageTitle,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatDate,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { useCreateQuestionnaireVersion, useQuestionnaireVersions } from './api'
import {
  questionCountLine,
  versionPublicationLine,
  versionStatus,
  versionTitle,
} from './questionnaire-labels'

/**
 * Questionário padrão da Westhetics: as versões (docs/areas/anamnese.md, "Telas · Questionário",
 * P-ANA-66; RN-ANA-18). Arquétipo: lista de trabalho, sem filtros: as versões são poucas e o
 * rascunho é um só, no topo. Uma ação primária: criar o rascunho, ou continuar o que existe.
 */
export function QuestionnaireVersionsPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const query = useQuestionnaireVersions({ page })
  const create = useCreateQuestionnaireVersion()

  const versions = query.data?.data ?? []
  const draft = versions.find((version) => version.status === 'draft')
  const current = versions.find((version) => version.isCurrent)
  const total = query.data?.meta.total ?? 0

  function createDraft() {
    create.mutate(
      {},
      {
        onSuccess: (version) => navigate(routes.admin.questionnaireVersion(version.id)),
      },
    )
  }

  const subtitle = query.data
    ? [
        `${total} ${total === 1 ? 'versão' : 'versões'}`,
        current?.publishedAt
          ? `versão ${current.number} vigente desde ${formatDate(new Date(current.publishedAt), { year: true })}`
          : 'nenhuma versão vigente',
        draft ? '1 rascunho em edição' : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'O que toda paciente responde antes do atendimento, em todas as clínicas.'

  const action = draft ? (
    <Button size="sm" asChild>
      <Link to={routes.admin.questionnaireVersion(draft.id)}>Continuar o rascunho</Link>
    </Button>
  ) : (
    <Button
      size="sm"
      loading={create.isPending}
      loadingLabel="Criando o rascunho…"
      onClick={createDraft}
    >
      + Criar rascunho
    </Button>
  )

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Westhetics' }, { label: 'Questionário padrão' }]}
        actions={query.data ? action : null}
      />
      <Page wide>
        <PageTitle title="Questionário padrão" subtitle={subtitle} />

        {create.isError ? (
          <ActionError
            className="mt-6"
            title="O rascunho não foi criado"
            description={errorMessage(create.error)}
            actions={
              draft ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to={routes.admin.questionnaireVersion(draft.id)}>Abrir o rascunho</Link>
                </Button>
              ) : undefined
            }
            onDismiss={() => create.reset()}
          />
        ) : null}

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar as versões"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : query.isPending ? (
          <div className="mt-6">
            <TableSkeleton rows={4} columns={4} />
          </div>
        ) : versions.length === 0 ? (
          <EmptyState
            className="mt-6"
            title="Nenhuma versão ainda"
            description="A versão é o questionário exato que toda paciente responde. Ela nasce de um rascunho publicado e não muda mais: corrigir é publicar outra."
            action={
              <Button
                loading={create.isPending}
                loadingLabel="Criando o rascunho…"
                onClick={createDraft}
              >
                + Criar rascunho
              </Button>
            }
          />
        ) : (
          <div className="mt-6">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead>Versão</TableHead>
                  <TableHead>Perguntas</TableHead>
                  <TableHead>Publicação</TableHead>
                  <TableHead>Status</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {versions.map((version) => {
                  const status = versionStatus(version)
                  return (
                    <TableRow
                      key={version.id}
                      onClick={() => navigate(routes.admin.questionnaireVersion(version.id))}
                    >
                      <TableCell primary>{versionTitle(version)}</TableCell>
                      <TableCell label="Perguntas" muted>
                        {questionCountLine(version.questionCount)}
                      </TableCell>
                      <TableCell label="Publicação" muted>
                        {versionPublicationLine(version)}
                      </TableCell>
                      <TableCell label="Status">
                        <StatusTag level={status.level}>{status.label}</StatusTag>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            {query.data ? (
              <ListFooter
                meta={query.data.meta}
                noun={['versão', 'versões']}
                note="da mais nova para a mais antiga · a linha abre a versão"
                onPage={setPage}
              />
            ) : null}
          </div>
        )}
      </Page>
    </>
  )
}
